import { PageOrientation } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { WorkOrderFlowTriggerConditions } from "../work-order-flow/work-order-flow";
import {
  AlignItems,
  Alignment,
  BomDisplayMode,
  OrderItemSort,
  OrderItemTableColumnType,
  PackingSlipBlockType,
  PackingSlipFontWeight,
  PackingSlipSortDirection,
  PageSize,
  QrCodeSize,
} from "./packing-slip-builder";

const baseSectionSchema = z.object({
  sectionPadding: z
    .object({
      top: z.number().meta({ privacy: privacy.nonPii }),
      right: z.number().meta({ privacy: privacy.nonPii }),
      bottom: z.number().meta({ privacy: privacy.nonPii }),
      left: z.number().meta({ privacy: privacy.nonPii }),
    })
    .meta({ privacy: privacy.structured }),
  alignSelf: createEnum(AlignItems, { enumName: "AlignItems" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  blockId: z.string().meta({ privacy: privacy.nonPii }),
  sectionColor: z.string().nullish().meta({ privacy: privacy.nonPii }),
});

const fontSectionSchema = baseSectionSchema.extend({
  fontFamily: z.string().nullish().meta({ privacy: privacy.nonPii }),
  fontSize: z.number().nullish().meta({ privacy: privacy.nonPii }),
  textColor: z.string().nullish().meta({ privacy: privacy.nonPii }),
  fontWeight: createEnum(PackingSlipFontWeight, {
    enumName: "PackingSlipFontWeight",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
});

const addressSectionSchema = fontSectionSchema.extend({
  showHeader: z.boolean().nullish().meta({ privacy: privacy.flag }),
  header: z.string().nullish().meta({ privacy: privacy.text.merchant }),
  showName: z.boolean().nullish().meta({ privacy: privacy.flag }),
  boldName: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showCompany: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showAddress: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showPhone: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showEmail: z.boolean().nullish().meta({ privacy: privacy.flag }),
});

const billingAddressSchema = addressSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.BILLING_ADDRESS)
    .meta({ privacy: privacy.nonPii }),
});

const companyAddressSchema = addressSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.COMPANY_ADDRESS)
    .meta({ privacy: privacy.nonPii }),
});

const contactInformationSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.CONTACT_INFORMATION)
    .meta({ privacy: privacy.nonPii }),
  showName: z.boolean().meta({ privacy: privacy.flag }),
  showCompany: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showAddress: z.boolean().meta({ privacy: privacy.flag }),
  showPhone: z.boolean().meta({ privacy: privacy.flag }),
  showEmail: z.boolean().meta({ privacy: privacy.flag }),
});

const noteFromCustomerSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.NOTE_FROM_CUSTOMER)
    .meta({ privacy: privacy.nonPii }),
});

const noteToCustomerSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.NOTE_TO_CUSTOMER)
    .meta({ privacy: privacy.nonPii }),
});

const orderBarcodeSchema = baseSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.ORDER_BARCODE)
    .meta({ privacy: privacy.nonPii }),
  width: z.number().nullish().meta({ privacy: privacy.nonPii }),
  height: z.number().nullish().meta({ privacy: privacy.nonPii }),
  useOrderNumber: z.boolean().nullish().meta({ privacy: privacy.flag }),
  useSecondaryOrderNumber: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  removeMarketplaceIdentifier: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.nonPii }), // removes BC-, EBAY-, CS- etc. Will not work within redo
  useCustomerName: z.boolean().nullish().meta({ privacy: privacy.flag }),
  useCustomerId: z.boolean().nullish().meta({ privacy: privacy.flag }),
  useBoxBarcode: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showAnnotation: z.boolean().nullish().meta({ privacy: privacy.flag }),
});

const brandNameSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.BRAND_NAME)
    .meta({ privacy: privacy.nonPii }),
});

const orderDateSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.ORDER_DATE)
    .meta({ privacy: privacy.nonPii }),
});

const printedDateSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.PRINTED_DATE)
    .meta({ privacy: privacy.nonPii }),
  showPrefix: z.boolean().nullish().meta({ privacy: privacy.flag }),
});

const orderItemsSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.ORDER_ITEMS)
    .meta({ privacy: privacy.nonPii }),
  sort: createEnum(OrderItemSort, { enumName: "OrderItemSort" }).meta({
    privacy: privacy.nonPii,
  }),
  customSortField: z.string().nullish().meta({ privacy: privacy.nonPii }),
  sortDirection: createEnum(PackingSlipSortDirection, {
    enumName: "PackingSlipSortDirection",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  tableColumns: z
    .array(
      z.object({
        type: createEnum(OrderItemTableColumnType, {
          enumName: "OrderItemTableColumnType",
        }).meta({ privacy: privacy.nonPii }),
        enabled: z.boolean().meta({ privacy: privacy.flag }),
        id: z.string().nullish().meta({ privacy: privacy.nonPii }),
        title: z.string().nullish().meta({ privacy: privacy.text.merchant }),
        displayName: z
          .string()
          .nullish()
          .meta({ privacy: privacy.text.merchant }),
        relativeFontSize: z
          .number()
          .nullish()
          .meta({ privacy: privacy.nonPii }),
        fontWeight: createEnum(PackingSlipFontWeight, {
          enumName: "PackingSlipFontWeight",
        })
          .nullish()
          .meta({ privacy: privacy.nonPii }),
        alignment: createEnum(Alignment, { enumName: "Alignment" })
          .nullish()
          .meta({ privacy: privacy.nonPii }),
      }),
    )
    .meta({ privacy: privacy.structured }),
  includeMetafields: z.boolean().nullish().meta({ privacy: privacy.flag }),
  rowHeight: z.number().nullish().meta({ privacy: privacy.nonPii }),
  emphasizeQuantity: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showDescription: z.boolean().nullish().meta({ privacy: privacy.flag }),
  includeHeader: z.boolean().nullish().meta({ privacy: privacy.flag }),
  tableBorderColor: z.string().nullish().meta({ privacy: privacy.nonPii }),
  tableBorderWidth: z.number().nullish().meta({ privacy: privacy.nonPii }),
  showVerticalColumnLines: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  customAttributes: z
    .array(z.string())
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  showAllCustomAttributes: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  separateAttributesVertically: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  makeAttributeNamesBold: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  splitItemsToSingleQuantity: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  showOnlyProductTitle: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showDiscountedUnitPrice: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.flag }),
  bomDisplay: createEnum(BomDisplayMode, { enumName: "BomDisplayMode" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  lineItemNotes: z
    .array(
      z.object({
        text: z.string().meta({ privacy: privacy.text.merchant }),
        conditions: WorkOrderFlowTriggerConditions.meta({
          privacy: privacy.untypedPayload,
        }),
      }),
    )
    .nullish()
    .meta({ privacy: privacy.structured }),
});

const orderDetailsSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.ORDER_DETAILS)
    .meta({ privacy: privacy.nonPii }),
  showRecipientInformation: z.boolean().meta({ privacy: privacy.flag }),
  showOrderNumber: z.boolean().meta({ privacy: privacy.flag }),
  showOrderDate: z.boolean().meta({ privacy: privacy.flag }),
  showShippingService: z.boolean().meta({ privacy: privacy.flag }),
  stacked: z.boolean().nullish().meta({ privacy: privacy.flag }),
});

const orderNumberSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.ORDER_NUMBER)
    .meta({ privacy: privacy.nonPii }),
});

const packageInformationSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.PACKAGE_INFORMATION)
    .meta({ privacy: privacy.nonPii }),
  showHeader: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showName: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showDimensions: z.boolean().nullish().meta({ privacy: privacy.flag }),
});

const paymentBreakdownSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.PAYMENT_BREAKDOWN)
    .meta({ privacy: privacy.nonPii }),
});

const paymentMethodSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.PAYMENT_METHOD)
    .meta({ privacy: privacy.nonPii }),
  showSubtotal: z.boolean().meta({ privacy: privacy.flag }),
  showPaymentMethod: z.boolean().meta({ privacy: privacy.flag }),
  showPaymentDate: z.boolean().meta({ privacy: privacy.flag }),
});

const packageWeightSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.PACKAGE_WEIGHT)
    .meta({ privacy: privacy.nonPii }),
});

const shippingAddressSchema = addressSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.SHIPPING_ADDRESS)
    .meta({ privacy: privacy.nonPii }),
});

const requestedShippingMethodSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.REQUESTED_SHIPPING_METHOD)
    .meta({ privacy: privacy.nonPii }),
});

const shippingMethodSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.SHIPPING_METHOD)
    .meta({ privacy: privacy.nonPii }),
});

const trackingNumberSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.TRACKING_NUMBER)
    .meta({ privacy: privacy.nonPii }),
});

const textSchema = baseSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.TEXT).meta({ privacy: privacy.nonPii }),
  textColor: z.string().meta({ privacy: privacy.nonPii }),
  fontSize: z.number().meta({ privacy: privacy.nonPii }),
  fontFamily: z.string().meta({ privacy: privacy.nonPii }),
  fontWeight: createEnum(PackingSlipFontWeight, {
    enumName: "PackingSlipFontWeight",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  linkColor: z.string().meta({ privacy: privacy.nonPii }),
  text: z.string().meta({ privacy: privacy.text.merchant }),
});

const lineSchema = baseSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.LINE).meta({ privacy: privacy.nonPii }),
  color: z.string().meta({ privacy: privacy.nonPii }),
});

const spacerSchema = baseSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.SPACER)
    .meta({ privacy: privacy.nonPii }),
  height: z.number().meta({ privacy: privacy.nonPii }),
});

const fillSpacerSchema = baseSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.FILL_SPACER)
    .meta({ privacy: privacy.nonPii }),
  minHeight: z.number().meta({ privacy: privacy.nonPii }),
});

const imageSchema = baseSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.IMAGE).meta({ privacy: privacy.nonPii }),
  imageUrl: z.string().meta({ privacy: privacy.publicUrl }),
  showCaption: z.boolean().meta({ privacy: privacy.flag }),
  caption: z.string().nullish().meta({ privacy: privacy.text.merchant }),
  altText: z.string().nullish().meta({ privacy: privacy.text.merchant }),
  clickthroughUrl: z.string().nullish().meta({ privacy: privacy.publicUrl }),
  aspectRatio: z.number().nullish().meta({ privacy: privacy.nonPii }),
  maxHeight: z.number().nullish().meta({ privacy: privacy.nonPii }),
  resolution: z.number().nullish().meta({ privacy: privacy.nonPii }),
});

const qrCodeSchema = baseSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.QR_CODE)
    .meta({ privacy: privacy.nonPii }),
  url: z.string().meta({ privacy: privacy.publicUrl }),
  size: createEnum(QrCodeSize, { enumName: "QrCodeSize" }).meta({
    privacy: privacy.nonPii,
  }),
  useReturnsPortalUrl: z.boolean().meta({ privacy: privacy.flag }),
});

const logoSchema = baseSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.LOGO).meta({ privacy: privacy.nonPii }),
  maxHeight: z.number().nullish().meta({ privacy: privacy.nonPii }),
  imageUrl: z.string().nullish().meta({ privacy: privacy.publicUrl }),
});

const headerMarkerSchema = baseSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.HEADER_MARKER)
    .meta({ privacy: privacy.nonPii }),
});

const weatherForecastSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.WEATHER_FORECAST)
    .meta({ privacy: privacy.nonPii }),
  showHigh: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showLow: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showIcon: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showShortText: z.boolean().nullish().meta({ privacy: privacy.flag }),
  showPrecipitationChance: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
});

const internalNotesSchema = fontSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.INTERNAL_NOTES)
    .meta({ privacy: privacy.nonPii }),
  showHeader: z.boolean().nullish().meta({ privacy: privacy.flag }),
});

const sectionWithoutColumnSchema = z.discriminatedUnion("type", [
  billingAddressSchema,
  companyAddressSchema,
  brandNameSchema,
  contactInformationSchema,
  noteFromCustomerSchema,
  noteToCustomerSchema,
  orderBarcodeSchema,
  orderDateSchema,
  printedDateSchema,
  orderItemsSchema,
  orderDetailsSchema,
  orderNumberSchema,
  packageInformationSchema,
  paymentBreakdownSchema,
  paymentMethodSchema,
  packageWeightSchema,
  shippingAddressSchema,
  requestedShippingMethodSchema,
  shippingMethodSchema,
  trackingNumberSchema,
  textSchema,
  lineSchema,
  spacerSchema,
  fillSpacerSchema,
  imageSchema,
  logoSchema,
  headerMarkerSchema,
  qrCodeSchema,
  weatherForecastSchema,
  internalNotesSchema,
]);

const columnSchema = baseSectionSchema.extend({
  type: z
    .literal(PackingSlipBlockType.COLUMN)
    .meta({ privacy: privacy.nonPii }),
  columns: z
    .array(z.lazy(() => sectionWithoutColumnSchema).nullish())
    .meta({ privacy: privacy.untypedPayload }),
  columnCount: z.number().meta({ privacy: privacy.nonPii }),
  gap: z.number().meta({ privacy: privacy.nonPii }),
});

export const packingSlipBlockSchemas: Record<
  PackingSlipBlockType,
  z.ZodObject<z.ZodRawShape>
> = {
  [PackingSlipBlockType.BILLING_ADDRESS]: billingAddressSchema,
  [PackingSlipBlockType.COMPANY_ADDRESS]: companyAddressSchema,
  [PackingSlipBlockType.BRAND_NAME]: brandNameSchema,
  [PackingSlipBlockType.CONTACT_INFORMATION]: contactInformationSchema,
  [PackingSlipBlockType.NOTE_FROM_CUSTOMER]: noteFromCustomerSchema,
  [PackingSlipBlockType.NOTE_TO_CUSTOMER]: noteToCustomerSchema,
  [PackingSlipBlockType.ORDER_BARCODE]: orderBarcodeSchema,
  [PackingSlipBlockType.ORDER_DATE]: orderDateSchema,
  [PackingSlipBlockType.PRINTED_DATE]: printedDateSchema,
  [PackingSlipBlockType.ORDER_ITEMS]: orderItemsSchema,
  [PackingSlipBlockType.ORDER_DETAILS]: orderDetailsSchema,
  [PackingSlipBlockType.ORDER_NUMBER]: orderNumberSchema,
  [PackingSlipBlockType.PACKAGE_INFORMATION]: packageInformationSchema,
  [PackingSlipBlockType.PAYMENT_BREAKDOWN]: paymentBreakdownSchema,
  [PackingSlipBlockType.PAYMENT_METHOD]: paymentMethodSchema,
  [PackingSlipBlockType.PACKAGE_WEIGHT]: packageWeightSchema,
  [PackingSlipBlockType.SHIPPING_ADDRESS]: shippingAddressSchema,
  [PackingSlipBlockType.REQUESTED_SHIPPING_METHOD]:
    requestedShippingMethodSchema,
  [PackingSlipBlockType.SHIPPING_METHOD]: shippingMethodSchema,
  [PackingSlipBlockType.TRACKING_NUMBER]: trackingNumberSchema,
  [PackingSlipBlockType.TEXT]: textSchema,
  [PackingSlipBlockType.LINE]: lineSchema,
  [PackingSlipBlockType.SPACER]: spacerSchema,
  [PackingSlipBlockType.FILL_SPACER]: fillSpacerSchema,
  [PackingSlipBlockType.IMAGE]: imageSchema,
  [PackingSlipBlockType.LOGO]: logoSchema,
  [PackingSlipBlockType.HEADER_MARKER]: headerMarkerSchema,
  [PackingSlipBlockType.QR_CODE]: qrCodeSchema,
  [PackingSlipBlockType.WEATHER_FORECAST]: weatherForecastSchema,
  [PackingSlipBlockType.INTERNAL_NOTES]: internalNotesSchema,
  [PackingSlipBlockType.COLUMN]: columnSchema,
};

// Finally, combine all schemas
const sectionSchema_ = z.discriminatedUnion("type", [
  ...sectionWithoutColumnSchema._def.options,
  columnSchema,
]);
export const sectionSchema: z.ZodType<PackingSlipSection> = sectionSchema_;

export type PackingSlipSection = z.infer<typeof sectionSchema_>;

const pageLayoutSchema_ = z.object({
  orientation: createEnum(PageOrientation, {
    enumName: "PageOrientation",
  }).meta({ privacy: privacy.nonPii }),
  size: createEnum(PageSize, { enumName: "PageSize" }).meta({
    privacy: privacy.nonPii,
  }),
});
export const pageLayoutSchema: z.ZodType<PackingSlipPageLayout> =
  pageLayoutSchema_;
export interface PackingSlipPageLayout extends z.infer<
  typeof pageLayoutSchema_
> {}

export interface IPackingSlipPageLayout extends z.ZodType<PackingSlipPageLayout> {}

export const packingSlipSchema_ = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.id.internal }),
  name: z.string().meta({ privacy: privacy.resourceLabel }),
  sections: z.array(sectionSchema).meta({ privacy: privacy.structured }),
  team: zExt.objectId().meta({ privacy: privacy.id.internal }),
  updatedAt: z.string().meta({ privacy: privacy.timestamp }),
  pageLayout: pageLayoutSchema.meta({
    privacy: privacy.structured,
  }) as IPackingSlipPageLayout,
  isLocalPickUpLabel: z.boolean().nullish().meta({ privacy: privacy.flag }),
  combineHalfPageSlips: z.boolean().nullish().meta({ privacy: privacy.flag }),
  pinFooterToBottom: z.boolean().nullish().meta({ privacy: privacy.flag }),
  deletedAt: z.string().optional().meta({ privacy: privacy.timestamp }),
});
export const packingSlipSchema: z.ZodType<PackingSlip> = packingSlipSchema_;
export interface PackingSlip extends z.infer<typeof packingSlipSchema_> {}
